import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, Download, Loader2, Images, X, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { handlePdfError } from "../../lib/pdf";
import { useAuth } from "../../context/AuthContext";
import { opCost, creditLabel } from "../../lib/pricing";
import { useOnline } from "../../hooks/usePWA";
import { Button } from "../ui/button";
import ToolIntro from "./ToolIntro";
import PreviewUnlock from "./PreviewUnlock";

const RATIOS = [
  { key: "portrait", label: "Portrait 4:5", hint: "1080×1350 — feed carousels" },
  { key: "square", label: "Square 1:1", hint: "1080×1080 — classic post" },
  { key: "story", label: "Story 9:16", hint: "1080×1920 — Stories / Reels" },
];

const okExt = (n) => /\.(pdf|pptx)$/i.test(n || "");

export default function CarouselTab() {
  const [file, setFile] = useState(null);
  const [ratio, setRatio] = useState("portrait");
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [convertError, setConvertError] = useState(null);
  const inputRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const online = useOnline();

  const cost = estimate?.total_cost ?? opCost(user, "carousel");

  const pick = async (f) => {
    if (!f) return;
    if (!okExt(f.name)) { toast.error("Please choose a PDF or PowerPoint (.pptx) file"); return; }
    setFile(f); setEstimate(null); setConvertError(null);
    try {
      const form = new FormData();
      form.append("kind", "carousel");
      form.append("file", f);
      const res = await api.post("/pdf/estimate", form, { headers: { "Content-Type": "multipart/form-data" } });
      setEstimate(res.data);
    } catch (e) { console.error("Cost estimate failed:", e); /* non-blocking */ }
  };

  const generate = async () => {
    if (!file) { toast.error("Add a file first"); return; }
    setLoading(true);
    setConvertError(null);
    try {
      const form = new FormData();
      form.append("ratio", ratio);
      form.append("file", file);
      const res = await api.post("/pdf/carousel", form, { headers: { "Content-Type": "multipart/form-data" } });
      setStage(res.data);
    } catch (e) {
      if (e?.response?.status === 422) {
        setConvertError(e?.response?.data?.detail || "We couldn't render this PowerPoint.");
      } else if (handlePdfError(e) === "no-credits") {
        navigate("/buy-credits");
      }
    } finally {
      setLoading(false);
    }
  };

  const clearFile = () => { setFile(null); setEstimate(null); setConvertError(null); };

  return (
    <div className="max-w-2xl mx-auto">
      <ToolIntro
        Icon={Images}
        title="Slide Deck → Social Carousel"
        desc="Turn a slide deck or PDF into ready-to-post images, each rendered at the exact social ratio and numbered for easy uploading."
        accepts="PDF or PowerPoint (.pptx)"
        tier="Standard"
        costLabel={`${creditLabel(cost)}${estimate?.pages ? ` · ${estimate.pages} slides` : ""}`}
      />

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        data-testid="carousel-dropzone"
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
          drag ? "border-indigo-500 bg-indigo-50" : "border-slate-300 bg-white hover:border-indigo-400 hover:bg-slate-50"
        }`}
      >
        <input ref={inputRef} data-testid="carousel-input" type="file" accept=".pdf,.pptx" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
        <div className="h-14 w-14 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4"><UploadCloud className="h-7 w-7 text-indigo-600" /></div>
        <p className="font-display text-lg font-semibold">{file ? file.name : "Drop a deck or click to browse"}</p>
        <p className="text-slate-500 text-sm mt-1">{file ? "Ready to slice into images" : "Each page becomes one numbered PNG."}</p>
      </div>

      {file && (
        <div className="mt-5">
          <p className="text-xs font-medium text-slate-500 mb-2">Output size</p>
          <div className="grid sm:grid-cols-3 gap-2">
            {RATIOS.map((r) => (
              <button
                key={r.key}
                data-testid={`carousel-ratio-${r.key}`}
                onClick={() => setRatio(r.key)}
                className={`text-left rounded-xl border p-3 transition-colors ${
                  ratio === r.key ? "border-indigo-600 ring-1 ring-indigo-600 bg-indigo-50" : "border-slate-200 bg-white hover:border-indigo-300"
                }`}
              >
                <div className="text-sm font-semibold">{r.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{r.hint}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-center gap-3">
        <Button data-testid="generate-carousel-button" onClick={generate} disabled={loading || !file || !online} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-8 h-12">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Create preview
        </Button>
        {file && (
          <button data-testid="carousel-reset" onClick={clearFile} className="text-slate-400 hover:text-red-500 flex items-center gap-1 text-sm">
            <X className="h-4 w-4" /> Clear
          </button>
        )}
      </div>

      {convertError && (
        <div data-testid="carousel-convert-error" className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">We couldn't render this PowerPoint</p>
              <p className="text-sm text-amber-800 mt-1">{convertError}</p>
              <p className="text-xs text-amber-700 mt-2">
                Tip: open your deck in PowerPoint, Keynote or Google Slides, export it as a <strong>PDF</strong>, then upload that here — it keeps every visual perfectly.
              </p>
              <Button
                data-testid="carousel-upload-pdf-instead"
                onClick={() => { clearFile(); setTimeout(() => inputRef.current?.click(), 0); }}
                variant="outline"
                className="mt-3 rounded-full border-amber-300 text-amber-800 hover:bg-amber-100 h-9"
              >
                <UploadCloud className="h-4 w-4 mr-2" /> Upload a PDF instead
              </Button>
            </div>
          </div>
        </div>
      )}

      {stage && <PreviewUnlock stage={stage} onClose={() => setStage(null)} onUnlocked={() => { setStage(null); clearFile(); }} />}
    </div>
  );
}
